import React, { useState } from 'react';
import { resourceAPI, riskAPI, documentAPI, changeRequestAPI, qualityCheckAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import {
  Users,
  AlertTriangle,
  FileText,
  GitPullRequest,
  CheckSquare,
  Plus,
  Trash2,
  Edit,
} from 'lucide-react';

export default function PlanningComponents({ projectId, resources, risks, documents, changeRequests, qualityChecks, onRefresh }) {
  const [activeTab, setActiveTab] = useState('resources');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [resourceForm, setResourceForm] = useState({ name: '', role: '', email: '', skills: '', hourly_rate: 0, availability: 100 });
  const [riskForm, setRiskForm] = useState({ name: '', description: '', probability: 'Medium', impact: 'Medium', mitigation: '', status: 'Open' });
  const [documentForm, setDocumentForm] = useState({ name: '', doc_type: 'Requirements', content: '', version: '1.0' });
  const [changeRequestForm, setChangeRequestForm] = useState({ title: '', description: '', change_type: 'Scope', impact: '', status: 'Pending' });
  const [qualityCheckForm, setQualityCheckForm] = useState({ name: '', check_type: 'Unit Test', status: 'Pending', notes: '' });

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    if (item) {
      switch (type) {
        case 'resource': setResourceForm({ ...item, skills: item.skills?.join(', ') || '' }); break;
        case 'risk': setRiskForm(item); break;
        case 'document': setDocumentForm(item); break;
        case 'changeRequest': setChangeRequestForm(item); break;
        case 'qualityCheck': setQualityCheckForm(item); break;
      }
    } else {
      setResourceForm({ name: '', role: '', email: '', skills: '', hourly_rate: 0, availability: 100 });
      setRiskForm({ name: '', description: '', probability: 'Medium', impact: 'Medium', mitigation: '', status: 'Open' });
      setDocumentForm({ name: '', doc_type: 'Requirements', content: '', version: '1.0' });
      setChangeRequestForm({ title: '', description: '', change_type: 'Scope', impact: '', status: 'Pending' });
      setQualityCheckForm({ name: '', check_type: 'Unit Test', status: 'Pending', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      switch (dialogType) {
        case 'resource':
          const resourceData = { ...resourceForm, skills: resourceForm.skills.split(',').map(s => s.trim()).filter(Boolean) };
          if (editItem) await resourceAPI.update(projectId, editItem.id, resourceData);
          else await resourceAPI.create(projectId, resourceData);
          break;
        case 'risk':
          if (editItem) await riskAPI.update(projectId, editItem.id, riskForm);
          else await riskAPI.create(projectId, riskForm);
          break;
        case 'document':
          if (editItem) await documentAPI.update(projectId, editItem.id, documentForm);
          else await documentAPI.create(projectId, documentForm);
          break;
        case 'changeRequest':
          if (editItem) await changeRequestAPI.update(projectId, editItem.id, changeRequestForm);
          else await changeRequestAPI.create(projectId, changeRequestForm);
          break;
        case 'qualityCheck':
          if (editItem) await qualityCheckAPI.update(projectId, editItem.id, qualityCheckForm);
          else await qualityCheckAPI.create(projectId, qualityCheckForm);
          break;
      }
      toast.success(editItem ? 'Updated successfully' : 'Created successfully');
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      switch (type) {
        case 'resource': await resourceAPI.delete(projectId, id); break;
        case 'risk': await riskAPI.delete(projectId, id); break;
        case 'document': await documentAPI.delete(projectId, id); break;
        case 'changeRequest': await changeRequestAPI.delete(projectId, id); break;
        case 'qualityCheck': await qualityCheckAPI.delete(projectId, id); break;
      }
      toast.success('Deleted successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'High': return 'bg-red-100 text-red-700';
      case 'Medium': return 'bg-amber-100 text-amber-700';
      case 'Low': return 'bg-green-100 text-green-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': case 'Pending': return 'bg-amber-100 text-amber-700';
      case 'Approved': case 'Passed': case 'Closed': return 'bg-green-100 text-green-700';
      case 'Rejected': case 'Failed': return 'bg-red-100 text-red-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="planning-components">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-zinc-900">Planning & Management</h2>
          <p className="text-zinc-600">Resources, Risks, Documents, Changes & Quality</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-zinc-200 rounded-xl p-1">
          <TabsTrigger value="resources" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" /> Resources ({resources?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="risks" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <AlertTriangle className="w-4 h-4 mr-2" /> Risks ({risks?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" /> Docs ({documents?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="changes" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <GitPullRequest className="w-4 h-4 mr-2" /> Changes ({changeRequests?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="quality" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white">
            <CheckSquare className="w-4 h-4 mr-2" /> Quality ({qualityChecks?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <Card className="bg-white border-zinc-200 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">Team Resources & Staffing</CardTitle>
              <Button onClick={() => openDialog('resource')} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> Add Resource
              </Button>
            </CardHeader>
            <CardContent>
              {resources?.length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No resources added yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Skills</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Availability</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resources?.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{r.role}</TableCell>
                        <TableCell>{r.email || '-'}</TableCell>
                        <TableCell>{r.skills?.join(', ') || '-'}</TableCell>
                        <TableCell>${r.hourly_rate}/hr</TableCell>
                        <TableCell>{r.availability}%</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('resource', r)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('resource', r.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks">
          <Card className="bg-white border-zinc-200 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">Risk Management</CardTitle>
              <Button onClick={() => openDialog('risk')} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> Add Risk
              </Button>
            </CardHeader>
            <CardContent>
              {risks?.length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No risks identified yet</p>
              ) : (
                <div className="grid gap-4">
                  {risks?.map(r => (
                    <Card key={r.id} className="bg-zinc-50 border-zinc-200 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{r.name}</h4>
                            <Badge className={getRiskColor(r.probability)}>P: {r.probability}</Badge>
                            <Badge className={getRiskColor(r.impact)}>I: {r.impact}</Badge>
                            <Badge className={getStatusColor(r.status)}>{r.status}</Badge>
                          </div>
                          <p className="text-sm text-zinc-600 mb-2">{r.description}</p>
                          {r.mitigation && <p className="text-sm"><strong>Mitigation:</strong> {r.mitigation}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openDialog('risk', r)}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('risk', r.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card className="bg-white border-zinc-200 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">Documentation</CardTitle>
              <Button onClick={() => openDialog('document')} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> Add Document
              </Button>
            </CardHeader>
            <CardContent>
              {documents?.length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No documents yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents?.map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{d.name}</TableCell>
                        <TableCell><Badge variant="outline">{d.doc_type}</Badge></TableCell>
                        <TableCell>v{d.version}</TableCell>
                        <TableCell>{formatDate(d.updated_at)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('document', d)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('document', d.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Change Requests Tab */}
        <TabsContent value="changes">
          <Card className="bg-white border-zinc-200 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">Change Requests</CardTitle>
              <Button onClick={() => openDialog('changeRequest')} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> New Request
              </Button>
            </CardHeader>
            <CardContent>
              {changeRequests?.length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No change requests</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Impact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requested By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changeRequests?.map(cr => (
                      <TableRow key={cr.id}>
                        <TableCell className="font-medium">{cr.title}</TableCell>
                        <TableCell><Badge variant="outline">{cr.change_type}</Badge></TableCell>
                        <TableCell className="max-w-[200px] truncate">{cr.impact}</TableCell>
                        <TableCell><Badge className={getStatusColor(cr.status)}>{cr.status}</Badge></TableCell>
                        <TableCell>{cr.requested_by}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('changeRequest', cr)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('changeRequest', cr.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quality Checks Tab */}
        <TabsContent value="quality">
          <Card className="bg-white border-zinc-200 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-outfit">Quality Management</CardTitle>
              <Button onClick={() => openDialog('qualityCheck')} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> Add Check
              </Button>
            </CardHeader>
            <CardContent>
              {qualityChecks?.length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No quality checks defined</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qualityChecks?.map(qc => (
                      <TableRow key={qc.id}>
                        <TableCell className="font-medium">{qc.name}</TableCell>
                        <TableCell><Badge variant="outline">{qc.check_type}</Badge></TableCell>
                        <TableCell><Badge className={getStatusColor(qc.status)}>{qc.status}</Badge></TableCell>
                        <TableCell className="max-w-[200px] truncate">{qc.notes || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openDialog('qualityCheck', qc)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('qualityCheck', qc.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for Add/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editItem ? 'Edit' : 'Add'} {dialogType === 'changeRequest' ? 'Change Request' : dialogType === 'qualityCheck' ? 'Quality Check' : dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}
            </DialogTitle>
            <DialogDescription>
              {editItem ? 'Update' : 'Create a new'} {dialogType === 'changeRequest' ? 'change request' : dialogType === 'qualityCheck' ? 'quality check' : dialogType} for this project
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {dialogType === 'resource' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Name *</Label><Input value={resourceForm.name} onChange={(e) => setResourceForm({...resourceForm, name: e.target.value})} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Role *</Label><Input value={resourceForm.role} onChange={(e) => setResourceForm({...resourceForm, role: e.target.value})} className="rounded-xl" /></div>
                </div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={resourceForm.email} onChange={(e) => setResourceForm({...resourceForm, email: e.target.value})} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Skills (comma separated)</Label><Input value={resourceForm.skills} onChange={(e) => setResourceForm({...resourceForm, skills: e.target.value})} placeholder="React, Python, AWS" className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Hourly Rate ($)</Label><Input type="number" value={resourceForm.hourly_rate} onChange={(e) => setResourceForm({...resourceForm, hourly_rate: parseFloat(e.target.value) || 0})} className="rounded-xl" /></div>
                  <div className="space-y-2"><Label>Availability (%)</Label><Input type="number" min="0" max="100" value={resourceForm.availability} onChange={(e) => setResourceForm({...resourceForm, availability: parseInt(e.target.value) || 100})} className="rounded-xl" /></div>
                </div>
              </>
            )}

            {dialogType === 'risk' && (
              <>
                <div className="space-y-2"><Label>Risk Name *</Label><Input value={riskForm.name} onChange={(e) => setRiskForm({...riskForm, name: e.target.value})} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Description *</Label><Textarea value={riskForm.description} onChange={(e) => setRiskForm({...riskForm, description: e.target.value})} className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Probability</Label>
                    <Select value={riskForm.probability} onValueChange={(v) => setRiskForm({...riskForm, probability: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Low">Low</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="High">High</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Impact</Label>
                    <Select value={riskForm.impact} onValueChange={(v) => setRiskForm({...riskForm, impact: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Low">Low</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="High">High</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Mitigation Plan</Label><Textarea value={riskForm.mitigation} onChange={(e) => setRiskForm({...riskForm, mitigation: e.target.value})} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={riskForm.status} onValueChange={(v) => setRiskForm({...riskForm, status: v})}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Open">Open</SelectItem><SelectItem value="Mitigated">Mitigated</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
                  </Select>
                </div>
              </>
            )}

            {dialogType === 'document' && (
              <>
                <div className="space-y-2"><Label>Document Name *</Label><Input value={documentForm.name} onChange={(e) => setDocumentForm({...documentForm, name: e.target.value})} className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={documentForm.doc_type} onValueChange={(v) => setDocumentForm({...documentForm, doc_type: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Requirements">Requirements</SelectItem><SelectItem value="Design">Design</SelectItem><SelectItem value="Test">Test</SelectItem><SelectItem value="Review">Review</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Version</Label><Input value={documentForm.version} onChange={(e) => setDocumentForm({...documentForm, version: e.target.value})} className="rounded-xl" /></div>
                </div>
                <div className="space-y-2"><Label>Content *</Label><Textarea value={documentForm.content} onChange={(e) => setDocumentForm({...documentForm, content: e.target.value})} className="rounded-xl min-h-[150px]" /></div>
              </>
            )}

            {dialogType === 'changeRequest' && (
              <>
                <div className="space-y-2"><Label>Title *</Label><Input value={changeRequestForm.title} onChange={(e) => setChangeRequestForm({...changeRequestForm, title: e.target.value})} className="rounded-xl" /></div>
                <div className="space-y-2"><Label>Description *</Label><Textarea value={changeRequestForm.description} onChange={(e) => setChangeRequestForm({...changeRequestForm, description: e.target.value})} className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Change Type</Label>
                    <Select value={changeRequestForm.change_type} onValueChange={(v) => setChangeRequestForm({...changeRequestForm, change_type: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Scope">Scope</SelectItem><SelectItem value="Schedule">Schedule</SelectItem><SelectItem value="Budget">Budget</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={changeRequestForm.status} onValueChange={(v) => setChangeRequestForm({...changeRequestForm, status: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Rejected">Rejected</SelectItem><SelectItem value="Implemented">Implemented</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Impact Assessment *</Label><Textarea value={changeRequestForm.impact} onChange={(e) => setChangeRequestForm({...changeRequestForm, impact: e.target.value})} className="rounded-xl" /></div>
              </>
            )}

            {dialogType === 'qualityCheck' && (
              <>
                <div className="space-y-2"><Label>Check Name *</Label><Input value={qualityCheckForm.name} onChange={(e) => setQualityCheckForm({...qualityCheckForm, name: e.target.value})} className="rounded-xl" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Check Type</Label>
                    <Select value={qualityCheckForm.check_type} onValueChange={(v) => setQualityCheckForm({...qualityCheckForm, check_type: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Unit Test">Unit Test</SelectItem><SelectItem value="Integration Test">Integration Test</SelectItem><SelectItem value="UAT">UAT</SelectItem><SelectItem value="Code Review">Code Review</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={qualityCheckForm.status} onValueChange={(v) => setQualityCheckForm({...qualityCheckForm, status: v})}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Passed">Passed</SelectItem><SelectItem value="Failed">Failed</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Notes</Label><Textarea value={qualityCheckForm.notes} onChange={(e) => setQualityCheckForm({...qualityCheckForm, notes: e.target.value})} className="rounded-xl" /></div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-primary hover:bg-orange-600 text-white rounded-lg">
                {loading ? <span className="spinner" /> : (editItem ? 'Update' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
